import { FormEvent, useRef, useState } from 'react'
import { api, ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'
import { checkRut } from '../../lib/rut'
import { Button, Hcaptcha, HcaptchaHandle, isCaptchaRequired, RutField, SelectField, SubmitButton, TextField } from '../../components/common'
import { AuthAlerts, AuthLayout, AuthLogo } from './AuthLayout'
import styles from './SignUpPage.module.scss'

const PROFESSION_OPTIONS: { id: number; title: string }[] = [
  { id: 6, title: 'Enfermero' },
  { id: 5, title: 'Fonoaudiólogo' },
  { id: 4, title: 'Kinesiólogo' },
  { id: 8, title: 'Matrón' },
  { id: 1, title: 'Médico general' },
  { id: 3, title: 'Nutricionista' },
  { id: 7, title: 'Oftalmólogo' },
  { id: 2, title: 'Pediatra' },
  { id: 0, title: 'Psicólogo' },
  { id: -1, title: 'Otro...' },
]

const STEPS = [
  { title: 'Paso 1', text: 'Escoge tu profesión' },
  { title: 'Paso 2', text: 'Ingresa tus datos' },
  { title: 'Paso 3', text: 'Escoge tu contraseña' },
]

type Fields = {
  professionId: number
  professionName: string
  name: string
  rut: string
  email: string
  phone: string
  password: string
  passwordConfirmation: string
}

export default function SignUpPage() {
  const [step, setStep] = useState(0)
  const [maxStep, setMaxStep] = useState(0)
  const [fields, setFields] = useState<Fields>({
    professionId: 1,
    professionName: '',
    name: '',
    rut: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirmation: '',
  })
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof Fields, string>>>({})
  const [errors, setErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [captchaSolved, setCaptchaSolved] = useState(false)
  const captchaRef = useRef<HcaptchaHandle | null>(null)
  const canSubmit = !submitting && (captchaSolved || !isCaptchaRequired())

  const setField = <K extends keyof Fields>(field: K, value: Fields[K]) => {
    setFields((current) => ({ ...current, [field]: value }))
    setFieldErrors((current) => ({ ...current, [field]: undefined }))
  }

  const goToStep = (target: number) => {
    if (target <= maxStep) {
      setStep(target)
    }
  }

  const validateStep2 = (): boolean => {
    const nextErrors: Partial<Record<keyof Fields, string>> = {}
    if (fields.name.trim().length === 0) {
      nextErrors.name = '¡Necesitamos tu nombre!'
    }
    if (fields.rut.trim().length === 0) {
      nextErrors.rut = '¡Necesitamos tu RUT!'
    } else if (!checkRut(fields.rut)) {
      nextErrors.rut = 'Rut Invalido'
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(fields.email)) {
      nextErrors.email = '¡Ingresa un e-mail correcto!'
    }
    if (fields.phone.trim().length === 0) {
      nextErrors.phone = '¡Necesitamos tu número de teléfono!'
    }
    setFieldErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const validateStep3 = (): boolean => {
    const nextErrors: Partial<Record<keyof Fields, string>> = {}
    if (fields.password.length < 8) {
      nextErrors.password = 'Ingresa una contraseña de al menos 8 caracteres'
    }
    if (fields.passwordConfirmation !== fields.password) {
      nextErrors.passwordConfirmation = 'Las contraseñas ingresadas no coinciden'
    }
    setFieldErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const advanceTo = (target: number) => {
    if (target === 1 || (target === 2 && validateStep2())) {
      trackEvent('Sign Up Step Completed', { step: target })
      setMaxStep((current) => Math.max(current, target))
      setStep(target)
    }
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting || !validateStep3()) {
      return
    }
    setSubmitting(true)
    setErrors([])
    trackEvent('Sign Up Submitted', { profession_id: fields.professionId })
    try {
      const result = await api.post<{ redirect: string }>('/professionals', {
        professional: {
          profession_id: fields.professionId,
          profession_name: fields.professionName,
          name: fields.name,
          rut: fields.rut,
          email: fields.email,
          phone: fields.phone,
          password: fields.password,
          password_confirmation: fields.passwordConfirmation,
        },
        'h-captcha-response': captchaRef.current?.getResponse() ?? '',
      })
      window.gtag?.('event', 'conversion', {
        send_to: 'AW-851627562/PRsCCN7g3ZkBEKqci5YD',
        transport_type: 'beacon',
      })
      window.location.assign(result.redirect)
    } catch (requestError) {
      const body = requestError instanceof ApiError ? (requestError.body as { error?: string; errors?: string[] } | null) : null
      trackEvent('Sign Up Failed', { reason: body?.errors?.join(', ') ?? body?.error ?? 'unknown' })
      setErrors(body?.errors ?? (body?.error ? [body.error] : ['Hubo un error al crear la cuenta']))
      captchaRef.current?.reset()
      setSubmitting(false)
      window.scrollTo({ top: 0 })
    }
  }

  return (
    <AuthLayout>
      <div className={`${styles.signupForm} col-md-8 col-md-offset-2 page well`} style={{ marginTop: 40 }}>
        <form role="form" onSubmit={(event) => void handleSubmit(event)}>
          <AuthAlerts notices={[]} errors={errors} />
          <AuthLogo narrow />
          <div className="row text-center">
            <p>¡Bienvenido a Historia Clínica!</p>
          </div>
          <div className={`row ${styles.formSpacing}`}>
            <div className="col-xs-12">
              <ul className="nav nav-pills nav-justified setup-panel">
                {STEPS.map((item, index) => (
                  <li key={index} className={step === index ? 'active' : index > maxStep ? 'disabled' : ''}>
                    <a
                      href={`#step-${index + 1}`}
                      onClick={(event) => {
                        event.preventDefault()
                        goToStep(index)
                      }}
                    >
                      <h4 className="list-group-item-heading">{item.title}</h4>
                      <p className="list-group-item-text">{item.text}</p>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {step === 0 && (
            <div id="step-1" className="row setup-content">
              <h2 className="col-md-12 text-center" style={{ marginTop: 40, color: '#000' }}>
                Selecciona tu profesión
              </h2>
              <div className="col-md-6">
                <SelectField
                  label="Profesión"
                  controlClassName="profession-selector"
                  controlStyle={{ width: '100%' }}
                  value={fields.professionId}
                  onChange={(value) => setField('professionId', Number(value))}
                  options={PROFESSION_OPTIONS.map((option) => ({ value: option.id, label: option.title }))}
                />
                {fields.professionId === -1 && (
                  <div className={`other-profession-fields ${styles.formSpacing} ${styles.professionHint}`}>
                    Indicanos cuál es tu profesión y te avisaremos cuando esté disponible.
                  </div>
                )}
              </div>
              {fields.professionId === -1 && (
                <div className="other-profession-fields col-md-6">
                  <TextField
                    label="Nombre de tu profesión"
                    value={fields.professionName}
                    onChange={(value) => setField('professionName', value)}
                  />
                </div>
              )}
              <div className="col-md-12">
                <Button id="activate-step-2" size="md" className={styles.stepButton} onClick={() => advanceTo(1)}>
                  Siguiente
                </Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div id="step-2" className="row setup-content">
              <h2 className="col-md-12 text-center" style={{ marginTop: 40, color: '#000' }}>
                Cuéntanos sobre ti
              </h2>
              <div className="col-md-6">
                <TextField label="Nombre" value={fields.name} error={fieldErrors.name} onChange={(value) => setField('name', value)} />
              </div>
              <div className="col-md-6">
                <RutField
                  label="RUT"
                  value={fields.rut}
                  error={fieldErrors.rut}
                  onChange={(value) => setField('rut', value)}
                />
              </div>
              <div className="col-md-6">
                <TextField
                  label="Email"
                  type="email"
                  id="inputEmail"
                  value={fields.email}
                  error={fieldErrors.email}
                  onChange={(value) => setField('email', value)}
                />
              </div>
              <div className="col-md-6">
                <TextField label="Teléfono" value={fields.phone} error={fieldErrors.phone} onChange={(value) => setField('phone', value)} />
              </div>
              <div className="col-md-12">
                <Button id="activate-step-3" size="md" className={styles.stepButton} onClick={() => advanceTo(2)}>
                  Siguiente
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div id="step-3" className="row setup-content">
              <h2 className="col-md-12 text-center" style={{ marginTop: 40, color: '#000' }}>
                ¡Ya casi terminas!
              </h2>
              <div className="col-md-12">
                <TextField
                  label="Contraseña"
                  type="password"
                  autoComplete="off"
                  id="inputPassword"
                  value={fields.password}
                  error={fieldErrors.password}
                  onChange={(value) => setField('password', value)}
                />
                <TextField
                  label="Confirme su nueva contraseña"
                  type="password"
                  id="inputPasswordConfirm"
                  value={fields.passwordConfirmation}
                  error={fieldErrors.passwordConfirmation}
                  onChange={(value) => setField('passwordConfirmation', value)}
                />
              </div>
              <div className="col-md-12">
                <Hcaptcha theme="light" handleRef={captchaRef} form="sign_up" onResponseChange={setCaptchaSolved} />
                <SubmitButton label="¡Crear tu cuenta!" id="sign-in-submit" block disabled={!canSubmit} />
              </div>
            </div>
          )}
        </form>
      </div>
    </AuthLayout>
  )
}
